import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import Plot from "react-plotly.js";

import Card from "components/atoms/Card";

export type UsageRoute = {
  routeKey: string;
  displayName: string;
  category: string;
  description?: string;
  totalCalls: number;
  totalTokens: number;
  avgDailyCalls: number;
  avgDailyTokens: number;
  avgTokensPerCall?: number;
  totalCostUSD?: number;
  avgDailyCostUSD?: number;
  costPerCall?: number;
  docsUrl?: string;
};

export type UsageBillingContext = {
  includedTokens?: number;
  currentUsageTokens?: number;
  remainingIncludedTokens?: number;
  projectedTokens?: number;
  pricePerMillion?: number;
  refillBatchTokens?: number;
  refillChargePerBatch?: number;
  projectedRefillCount?: number;
  projectedRefillCost?: number;
  daysRemaining?: number;
  projectionLabel?: string;
};

const FAMILY_COLORS: Record<string, string> = {
  Vision: "#16a34a",
  "Text AI": "#ea580c",
  Search: "#2563eb",
  "Food Database": "#0891b2",
  "Advisor / Chat": "#7c3aed",
  Catalog: "#db2777",
  Affiliates: "#0f766e",
  Other: "#6b7280",
};

const MONTH_DAYS = 30.44;
const CHART_LIMIT = 20;

type SortKey =
  | "name"
  | "calls"
  | "tokens"
  | "share"
  | "perCall"
  | "periodCost"
  | "projected";

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  if (num === 0) return "0";
  if (num < 1) return num.toFixed(2);
  return num.toFixed(0);
};

const formatExact = (num: number): string =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    Math.round(num)
  );

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount) || amount === 0) return "$0.00";
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
};

const formatPct = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 0.1) return "<0.1%";
  return `${value.toFixed(1)}%`;
};

export const routeFamily = (category: string, routeKey = ""): string => {
  const haystack = `${category} ${routeKey}`.toLowerCase();
  if (haystack.includes("vision")) return "Vision";
  if (haystack.includes("semantic") || haystack.includes("search"))
    return "Search";
  if (haystack.includes("catalog") || haystack.includes("chain"))
    return "Catalog";
  if (haystack.includes("affiliate")) return "Affiliates";
  if (
    haystack.includes("advisor") ||
    haystack.includes("chat") ||
    haystack.includes("easyai")
  )
    return "Advisor / Chat";
  if (haystack.includes("food")) return "Food Database";
  if (haystack.includes("ai tool") || haystack.includes("ai tools"))
    return "Text AI";
  return "Other";
};

type Props = {
  routes: UsageRoute[];
  days: number;
  tokenLabel?: string;
  billing?: UsageBillingContext;
};

const RouteUsageOverview = ({
  routes,
  days,
  tokenLabel = "tokens",
  billing,
}: Props) => {
  const [metric, setMetric] = useState<"calls" | "tokens">("tokens");
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("tokens");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const pricePerMillion = billing?.pricePerMillion || 0;
  const includedTokens = billing?.includedTokens || 0;
  const projectedTokens = billing?.projectedTokens || 0;
  const refillBatchTokens = billing?.refillBatchTokens || 0;
  const refillChargePerBatch = billing?.refillChargePerBatch || 0;
  const projectedRefillCount = billing?.projectedRefillCount || 0;
  const remainingIncludedTokens =
    billing?.remainingIncludedTokens ??
    Math.max(0, includedTokens - (billing?.currentUsageTokens || 0));
  const daysRemaining = billing?.daysRemaining;

  const used = useMemo(
    () =>
      routes.filter((route) => route.totalCalls > 0 || route.totalTokens > 0),
    [routes]
  );

  const families = useMemo(() => {
    const map = new Map<
      string,
      { calls: number; tokens: number; routes: number; cost: number }
    >();
    used.forEach((route) => {
      const family = routeFamily(route.category, route.routeKey);
      const current = map.get(family) || {
        calls: 0,
        tokens: 0,
        routes: 0,
        cost: 0,
      };
      current.calls += route.totalCalls;
      current.tokens += route.totalTokens;
      current.cost += route.totalCostUSD || 0;
      current.routes += 1;
      map.set(family, current);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [used]);

  const totalCalls = used.reduce((sum, route) => sum + route.totalCalls, 0);
  const totalTokens = used.reduce((sum, route) => sum + route.totalTokens, 0);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = used.filter((route) => {
      const family = routeFamily(route.category, route.routeKey);
      if (familyFilter && family !== familyFilter) return false;
      if (!needle) return true;
      return (
        route.displayName.toLowerCase().includes(needle) ||
        route.routeKey.toLowerCase().includes(needle) ||
        family.toLowerCase().includes(needle) ||
        (route.description || "").toLowerCase().includes(needle) ||
        (route.category || "").toLowerCase().includes(needle) ||
        (route.docsUrl || "").toLowerCase().includes(needle)
      );
    });

    const projectedCost = (route: UsageRoute) =>
      pricePerMillion > 0
        ? (route.avgDailyTokens * MONTH_DAYS * pricePerMillion) / 1_000_000
        : (route.avgDailyCostUSD || 0) * MONTH_DAYS;

    const valueOf = (route: UsageRoute): number | string => {
      switch (sortKey) {
        case "name":
          return route.displayName.toLowerCase();
        case "calls":
          return route.totalCalls;
        case "share":
          return totalTokens > 0 ? route.totalTokens / totalTokens : 0;
        case "perCall":
          return route.avgTokensPerCall || 0;
        case "periodCost":
          return route.totalCostUSD || 0;
        case "projected":
          return projectedCost(route);
        case "tokens":
        default:
          return route.totalTokens;
      }
    };

    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc"
        ? Number(av) - Number(bv)
        : Number(bv) - Number(av);
    });
  }, [
    used,
    search,
    familyFilter,
    sortKey,
    sortDir,
    totalTokens,
    pricePerMillion,
  ]);

  const chartRows = filtered.slice(0, CHART_LIMIT);
  const familyValues = families.map((family) =>
    metric === "tokens" ? family.tokens : family.calls
  );
  const includedUsedPct =
    includedTokens > 0
      ? ((billing?.currentUsageTokens || totalTokens) / includedTokens) * 100
      : 0;
  const projectedOverage = Math.max(0, projectedTokens - includedTokens);
  const projectedRefills =
    refillBatchTokens > 0 ? Math.ceil(projectedOverage / refillBatchTokens) : 0;
  const expectedRefillCount =
    billing?.projectedRefillCount != null
      ? projectedRefillCount
      : projectedRefills;
  const expectedRefillCost =
    billing?.projectedRefillCost != null
      ? billing.projectedRefillCost
      : expectedRefillCount * refillChargePerBatch;
  const projectedMonthCost =
    pricePerMillion > 0
      ? ((totalTokens / Math.max(days, 1)) * MONTH_DAYS * pricePerMillion) /
        1_000_000
      : 0;
  const chartMax = Math.max(
    ...chartRows.map((route) =>
      metric === "tokens" ? route.totalTokens : route.totalCalls
    ),
    1
  );

  const toggleSort = (next: SortKey) => {
    if (sortKey === next) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(next);
    setSortDir(next === "name" ? "asc" : "desc");
  };

  const SortHeader = ({
    id,
    label,
    align = "right",
  }: {
    id: SortKey;
    label: string;
    align?: "left" | "right";
  }) => (
    <th
      className={`py-2 px-3 cursor-pointer select-none ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={() => toggleSort(id)}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "justify-end" : ""
        }`}
      >
        {label}
        {sortKey === id &&
          (sortDir === "asc" ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : (
            <ChevronDownIcon className="h-3 w-3" />
          ))}
      </span>
    </th>
  );

  if (used.length === 0) {
    return (
      <Card className="p-6">
        <h4 className="font-semibold text-gray-900">Usage by route</h4>
        <p className="mt-2 text-sm text-gray-500">
          No API calls in this period yet. Usage by vision, text, search, and
          other groups will show up here after the first requests.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h4 className="font-semibold text-gray-900">Usage by route</h4>
          <p className="text-sm text-gray-500">
            Every route this account called in the selected period. Search or
            click a group to find a specific API. Path IDs are collapsed.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "calls"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("calls")}
          >
            Calls
          </button>
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "tokens"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("tokens")}
          >
            {tokenLabel}
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            This period
          </p>
          <p className="text-xl font-semibold text-gray-900">
            {formatNumber(totalTokens)}
          </p>
          <p className="text-xs text-gray-500">
            {formatExact(totalCalls)} calls across {used.length} routes
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Included budget
          </p>
          <p className="text-xl font-semibold text-gray-900">
            {includedTokens > 0 ? formatPct(includedUsedPct) : "—"}
          </p>
          <p className="text-xs text-gray-500">
            {includedTokens > 0
              ? `${formatNumber(
                  billing?.currentUsageTokens || totalTokens
                )} of ${formatNumber(includedTokens)} included`
              : "No included-token allotment on this plan"}
          </p>
        </div>
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-indigo-700">
            {billing?.projectionLabel || "30-day projection"}
          </p>
          <p className="text-xl font-semibold text-indigo-950">
            {projectedTokens > 0
              ? formatNumber(projectedTokens)
              : formatNumber((totalTokens / Math.max(days, 1)) * MONTH_DAYS)}
          </p>
          <p className="text-xs text-indigo-800">
            {includedTokens > 0
              ? projectedOverage > 0
                ? `${formatNumber(projectedOverage)} over included${
                    projectedRefills > 0
                      ? ` · ${projectedRefills} refill batch${
                          projectedRefills === 1 ? "" : "es"
                        }`
                      : ""
                  }`
                : "On pace to stay within included tokens"
              : `${formatCurrency(projectedMonthCost)} at refill rate`}
          </p>
        </div>
        <div
          className={`rounded-lg border px-3 py-2 ${
            expectedRefillCount > 0
              ? "border-amber-100 bg-amber-50/80"
              : "border-emerald-100 bg-emerald-50/70"
          }`}
        >
          <p
            className={`text-[11px] uppercase tracking-wide ${
              expectedRefillCount > 0 ? "text-amber-800" : "text-emerald-800"
            }`}
          >
            Expected extra this cycle
          </p>
          <p
            className={`text-xl font-semibold ${
              expectedRefillCount > 0 ? "text-amber-950" : "text-emerald-950"
            }`}
          >
            {formatCurrency(expectedRefillCost)}
          </p>
          <p
            className={`text-xs ${
              expectedRefillCount > 0 ? "text-amber-800" : "text-emerald-800"
            }`}
          >
            {expectedRefillCount > 0
              ? `${expectedRefillCount} refill batch${
                  expectedRefillCount === 1 ? "" : "es"
                } to cover ${formatNumber(projectedOverage)} over included${
                  refillChargePerBatch > 0
                    ? ` · ${formatCurrency(
                        refillChargePerBatch
                      )} per ${formatNumber(refillBatchTokens)} tokens`
                    : pricePerMillion > 0
                    ? ` at $${pricePerMillion.toFixed(2)}/M`
                    : ""
                }${
                  typeof daysRemaining === "number" && daysRemaining > 0
                    ? ` · ${Math.ceil(daysRemaining)} day${
                        Math.ceil(daysRemaining) === 1 ? "" : "s"
                      } left`
                    : ""
                }`
              : includedTokens > 0
              ? `No extra refills expected${
                  remainingIncludedTokens > 0
                    ? ` · ${formatNumber(
                        remainingIncludedTokens
                      )} included remaining`
                    : ""
                }${
                  typeof daysRemaining === "number" && daysRemaining > 0
                    ? ` · ${Math.ceil(daysRemaining)} day${
                        Math.ceil(daysRemaining) === 1 ? "" : "s"
                      } left`
                    : ""
                }`
              : pricePerMillion > 0
              ? `No included allotment · ${formatCurrency(
                  projectedMonthCost
                )} at $${pricePerMillion.toFixed(2)}/M if this pace holds`
              : "No refill pricing on this plan"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find a route: berg, vision, search, grocery..."
            className="pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md w-full bg-white"
          />
        </div>
        {familyFilter && (
          <button
            type="button"
            className="text-xs font-medium text-indigo-700 hover:underline"
            onClick={() => setFamilyFilter(null)}
          >
            Clear group filter
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {families.map((family) => {
          const active = familyFilter === family.name;
          return (
            <button
              key={family.name}
              type="button"
              onClick={() =>
                setFamilyFilter((prev) =>
                  prev === family.name ? null : family.name
                )
              }
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                active
                  ? "border-indigo-300 bg-indigo-50 text-indigo-800"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor:
                    FAMILY_COLORS[family.name] || FAMILY_COLORS.Other,
                }}
              />
              {family.name}
              <span className="text-gray-500">
                {formatNumber(
                  metric === "tokens" ? family.tokens : family.calls
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">
            By product group
          </p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            Full-account mix. Click a slice or chip to focus the table.
          </p>
          <Plot
            data={[
              {
                type: "pie",
                hole: 0.45,
                labels: families.map((family) => family.name),
                values: familyValues,
                marker: {
                  colors: families.map(
                    (family) =>
                      FAMILY_COLORS[family.name] || FAMILY_COLORS.Other
                  ),
                },
                textinfo: "label+percent",
                textposition: "inside",
                insidetextorientation: "horizontal",
                hovertemplate:
                  "%{label}<br>%{value:,} " +
                  metric +
                  "<br>%{percent}<extra></extra>",
              },
            ]}
            layout={{
              height: 300,
              margin: { t: 8, r: 8, b: 8, l: 8 },
              paper_bgcolor: "transparent",
              showlegend: false,
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: "100%", height: "300px" }}
            onClick={(event) => {
              const pointNumber = event?.points?.[0]?.pointNumber;
              const label =
                typeof pointNumber === "number"
                  ? families[pointNumber]?.name
                  : undefined;
              if (label) {
                setFamilyFilter((prev) => (prev === label ? null : label));
              }
            }}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">
            {familyFilter || search.trim()
              ? "Matching routes"
              : "Largest routes"}
          </p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            {filtered.length <= CHART_LIMIT
              ? `All ${filtered.length} matching routes.`
              : `Showing the top ${CHART_LIMIT} of ${filtered.length} matches. The table below has every row.`}{" "}
            Names shorten with … — hover for the full route.
          </p>
          {chartRows.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              No routes match that search.
            </p>
          ) : (
            <div className="space-y-1.5 pt-1">
              {chartRows.map((route) => {
                const value =
                  metric === "tokens" ? route.totalTokens : route.totalCalls;
                const family = routeFamily(route.category, route.routeKey);
                const widthPct = Math.max(
                  (value / chartMax) * 100,
                  value > 0 ? 2 : 0
                );
                const hover = [
                  route.displayName,
                  route.routeKey,
                  family,
                  `Calls: ${formatExact(route.totalCalls)}`,
                  `Tokens: ${formatNumber(route.totalTokens)}`,
                  `Avg/day: ${route.avgDailyCalls.toFixed(1)}`,
                  route.totalCostUSD
                    ? `Period $: ${formatCurrency(route.totalCostUSD)}`
                    : "",
                ]
                  .filter(Boolean)
                  .join("\n");
                return (
                  <div
                    key={route.routeKey}
                    className="group flex items-center gap-2"
                    title={hover}
                  >
                    <p
                      className="w-36 sm:w-44 shrink-0 truncate text-xs text-gray-800"
                      title={hover}
                    >
                      {route.displayName}
                    </p>
                    <div className="flex-1 min-w-0 h-5 rounded bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded"
                        style={{
                          width: `${widthPct}%`,
                          backgroundColor:
                            FAMILY_COLORS[family] || FAMILY_COLORS.Other,
                        }}
                      />
                    </div>
                    <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-gray-600">
                      {formatNumber(value)}
                    </span>
                    {route.docsUrl ? (
                      <a
                        href={route.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-indigo-500 hover:text-indigo-700"
                        title="Open OpenAPI docs for this route"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="w-3.5 shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900">
            All matching calls
          </p>
          <p className="text-xs text-gray-500">
            {filtered.length} route{filtered.length === 1 ? "" : "s"} · share is
            of this period&apos;s {tokenLabel.toLowerCase()} · 30d $ is this
            route&apos;s current daily pace at refill rate
          </p>
        </div>
        <div className="overflow-auto max-h-[480px]">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="border-b border-gray-200 text-xs text-gray-600">
                <SortHeader id="name" label="Route" align="left" />
                <th className="py-2 px-3 text-left">Group</th>
                <SortHeader id="calls" label="Calls" />
                <SortHeader id="tokens" label="Tokens" />
                <SortHeader id="share" label="Share" />
                <SortHeader id="perCall" label="Tok/call" />
                <SortHeader id="periodCost" label="Period $" />
                <SortHeader id="projected" label="30d proj $" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    No routes match. Try another name, group, or clear the
                    filter.
                  </td>
                </tr>
              ) : (
                filtered.map((route) => {
                  const family = routeFamily(route.category, route.routeKey);
                  const share =
                    totalTokens > 0
                      ? (route.totalTokens / totalTokens) * 100
                      : 0;
                  const projected =
                    pricePerMillion > 0
                      ? (route.avgDailyTokens * MONTH_DAYS * pricePerMillion) /
                        1_000_000
                      : (route.avgDailyCostUSD || 0) * MONTH_DAYS;
                  return (
                    <tr
                      key={route.routeKey}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-2 px-3">
                        <div className="flex items-start gap-1.5 min-w-0">
                          <div className="min-w-0">
                            <p
                              className="font-medium text-gray-900 truncate"
                              title={`${route.displayName}\n${route.routeKey}`}
                            >
                              {route.displayName}
                            </p>
                            {route.description ? (
                              <p
                                className="text-[11px] text-gray-500 line-clamp-1"
                                title={route.routeKey}
                              >
                                {route.description}
                              </p>
                            ) : (
                              <p
                                className="text-[11px] text-gray-400 truncate font-mono"
                                title={route.routeKey}
                              >
                                {route.routeKey}
                              </p>
                            )}
                          </div>
                          {route.docsUrl && (
                            <a
                              href={route.docsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 mt-0.5 text-indigo-500 hover:text-indigo-700"
                              title="Open OpenAPI docs for this route"
                            >
                              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-700">
                          <span
                            className="h-1.5 w-1.5 rounded-full"
                            style={{
                              backgroundColor:
                                FAMILY_COLORS[family] || FAMILY_COLORS.Other,
                            }}
                          />
                          {family}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatExact(route.totalCalls)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatNumber(route.totalTokens)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatPct(share)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-gray-600">
                        {formatNumber(route.avgTokensPerCall || 0)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {formatCurrency(route.totalCostUSD || 0)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-indigo-800">
                        {formatCurrency(projected)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

export default RouteUsageOverview;
